// src/pages/VendorAdminDashboard.js
import React, { useCallback, useEffect, useState } from 'react';
import { Row, Col } from 'react-bootstrap';
import axios from 'axios';
import DashboardShell from '../components/dashboard/DashboardShell';
import StatTile from '../components/dashboard/departmentadmin/StatTile';
import MonthlySessionsChart from '../components/dashboard/vendor/MonthlySessionsChart';
import TrainerUtilizationList from '../components/dashboard/vendor/TrainerUtilizationList';
import TrainingQuickListCard from '../components/dashboard/trainer/TrainingQuickListCard';
import TrainerTrainingsTable from '../components/dashboard/trainer/TrainerTrainingsTable';
import { getUser } from '../utils/auth';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function VendorAdminDashboard() {
  const user = getUser();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  // A trainingadmin may personally be assigned as a trainer on their
  // vendor's trainings too — reuses the trainer dashboard's own endpoint
  // and cards (get_trainer_dashboard.php is scoped by the caller's own
  // session id regardless of role) rather than duplicating that data/UI.
  const [trainerStats, setTrainerStats] = useState(null);
  const [trainerLoading, setTrainerLoading] = useState(true);

  const fetchTrainerStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/stat/get_trainer_dashboard.php`, {
        withCredentials: true
      });
      setTrainerStats(response.data);
    } catch (error) {
      console.error('Error fetching trainer-assignment stats:', error);
    } finally {
      setTrainerLoading(false);
    }
  }, []);

  useEffect(() => {
    let isMounted = true;

    const fetchStats = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/stat/get_vendor_dashboard_stats.php`, {
          withCredentials: true
        });
        if (isMounted) setStats(response.data);
      } catch (error) {
        console.error('Error fetching vendor dashboard:', error);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchStats();
    return () => { isMounted = false; };
  }, []);

  useEffect(() => {
    fetchTrainerStats();
  }, [fetchTrainerStats]);

  return (
    <DashboardShell title={`Welcome, ${user.name}`} subtitle="Your vendor overview">
      <Row className="g-3 mb-1">
        <Col md={4}>
          <StatTile title="Upcoming Trainings to Deliver" icon="bi-calendar-event" value={stats?.upcomingTrainingsCount ?? 0} loading={loading} />
        </Col>
        <Col md={4}>
          <StatTile title="Trainers on Upcoming Trainings" icon="bi-people" value={stats?.trainersAssignedCount ?? 0} loading={loading} />
        </Col>
        <Col md={4}>
          <StatTile title="Courses Offered" icon="bi-journal-bookmark" value={stats?.coursesOfferedCount ?? 0} loading={loading} />
        </Col>
      </Row>
      <Row className="g-3 mt-1">
        <Col md={6}>
          <MonthlySessionsChart data={stats?.monthlySessions ?? []} loading={loading} />
        </Col>
        <Col md={6}>
          <TrainerUtilizationList data={stats?.trainerUtilization ?? []} loading={loading} />
        </Col>
      </Row>

      <hr className="my-4" />
      <h5 className="mb-3">Your Own Trainer Assignments</h5>
      <Row className="g-3 mb-1">
        <Col md={6}>
          <TrainingQuickListCard
            title="Upcoming Sessions (next 30 days)"
            icon="bi-calendar-event"
            count={trainerStats?.upcomingCount ?? 0}
            items={trainerStats?.upcomingSessions ?? []}
            loading={trainerLoading}
            emptyMessage="No sessions in the next 30 days."
          />
        </Col>
        <Col md={6}>
          <TrainingQuickListCard
            title="Attendance Not Yet Submitted"
            icon="bi-exclamation-triangle"
            count={trainerStats?.pendingAttendanceCount ?? 0}
            items={trainerStats?.pendingAttendance ?? []}
            loading={trainerLoading}
            emptyMessage="Nothing pending — all caught up."
          />
        </Col>
      </Row>
      <Row className="g-3 mt-1">
        <Col md={12}>
          <TrainerTrainingsTable trainings={trainerStats?.allTrainings ?? []} loading={trainerLoading} onStatusChanged={fetchTrainerStats} />
        </Col>
      </Row>
    </DashboardShell>
  );
}

export default VendorAdminDashboard;
